import {
  ROTATION_PASTE_MORE_ABOVE,
  ROTATION_PASTE_MORE_BELOW,
} from 'constants/uiConstants';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { LuArrowDown, LuArrowUp } from 'react-icons/lu';
import { pxToRem } from 'utils/cssUnits';
import MergeClassNames from 'utils/MergeClassNames';
import type { GridPosition } from './rotationPaste';

/** Space between the panel and the cells, and the edge it's kept inside. */
const GAP_PX = 6;

interface Placement {
  top: number;
  left: number;
  above: GridPosition[];
  below: GridPosition[];
}

const cellSelector = ({ row, col }: GridPosition) =>
  `[data-rotation-cell="${row}:${col}"]`;

const samePlacement = (a: Placement | null, b: Placement) =>
  !!a &&
  a.top === b.top &&
  a.left === b.left &&
  a.above.length === b.above.length &&
  a.below.length === b.below.length;

interface RotationAnchoredPanelProps {
  /** The grid's scroll area — the panel stays inside its visible part. */
  containerRef: React.RefObject<HTMLElement>;
  /** The cells the panel speaks for; it sits under their bottom-right corner. */
  positions: GridPosition[];
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  children: React.ReactNode;
}

/**
 * A small panel pinned beside the grid cells it's about (OP-2501) — under
 * the bottom-right of a paste, a delete or the active cell — so its message
 * sits where the advisor is looking. When those cells scroll away it stays
 * at the nearest edge of the grid's visible area, and offers to jump to any
 * of them out of view ("2 more below").
 */
const RotationAnchoredPanel: React.FC<RotationAnchoredPanelProps> = ({
  containerRef,
  positions,
  onKeyDown,
  children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const positionsRef = useRef(positions);
  positionsRef.current = positions;
  const positionsKey = positions.map(({ row, col }) => `${row}:${col}`).join();

  useLayoutEffect(() => {
    const panel = panelRef.current;
    const container = containerRef.current;
    if (!panel || !container) return undefined;
    let frame = 0;

    const measure = () => {
      frame = 0;
      const box = container.getBoundingClientRect();
      const visibleTop = Math.max(box.top, 0);
      const visibleBottom = Math.min(box.bottom, window.innerHeight);
      const visibleLeft = Math.max(box.left, 0);
      const visibleRight = Math.min(box.right, window.innerWidth);
      const cells = positionsRef.current.flatMap((position) => {
        const element = container.querySelector(cellSelector(position));
        return element
          ? [{ position, rect: element.getBoundingClientRect() }]
          : [];
      });
      if (cells.length === 0) return;
      const bottom = Math.max(...cells.map((cell) => cell.rect.bottom));
      const right = Math.max(...cells.map((cell) => cell.rect.right));
      const { width, height } = panel.getBoundingClientRect();
      const clamp = (value: number, min: number, max: number) =>
        Math.min(Math.max(value, min), Math.max(max, min));
      const next: Placement = {
        top: clamp(
          bottom + GAP_PX,
          visibleTop + GAP_PX,
          visibleBottom - height - GAP_PX
        ),
        left: clamp(
          right - width,
          visibleLeft + GAP_PX,
          visibleRight - width - GAP_PX
        ),
        above: cells
          .filter((cell) => cell.rect.bottom <= visibleTop)
          .map((cell) => cell.position),
        below: cells
          .filter((cell) => cell.rect.top >= visibleBottom)
          .map((cell) => cell.position),
      };
      setPlacement((prev) => (samePlacement(prev, next) ? prev : next));
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };

    measure();
    // Capture, so scrolling the page or the grid's own area both move it.
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    const observer = new ResizeObserver(schedule);
    observer.observe(panel);
    observer.observe(container);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      observer.disconnect();
    };
  }, [containerRef, positionsKey]);

  const jumpTo = (position: GridPosition | undefined) => {
    if (!position) return;
    containerRef.current
      ?.querySelector(cellSelector(position))
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const jumpClass =
    'flex items-center gap-1 rounded px-1 text-sm font-semibold text-primary hover:bg-rowHover focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary';

  return (
    <div
      ref={panelRef}
      role="status"
      aria-live="polite"
      // Until it has been measured it is placed but not shown (still
      // focusable, so its default button can take focus straight away).
      className={MergeClassNames(
        'rotation-paste-bar fixed z-40 w-max max-w-[min(32rem,calc(100vw-2rem))] rounded-md border border-gray-bright bg-white px-3 py-2 shadow-lg',
        !placement && 'pointer-events-none opacity-0'
      )}
      // Runtime coordinates, consumed by .rotation-paste-bar in App.css
      style={
        {
          '--paste-bar-top': `${pxToRem(placement?.top ?? 0)}rem`,
          '--paste-bar-left': `${pxToRem(placement?.left ?? 0)}rem`,
        } as React.CSSProperties
      }
      onKeyDown={onKeyDown}
    >
      {children}
      {placement &&
        (placement.above.length > 0 || placement.below.length > 0) && (
          <div className="mt-1.5 flex gap-2 border-t border-gray-bright pt-1.5">
            {placement.above.length > 0 && (
              <button
                type="button"
                className={jumpClass}
                onClick={() =>
                  jumpTo(placement.above[placement.above.length - 1])
                }
              >
                <LuArrowUp size={14} aria-hidden />
                {ROTATION_PASTE_MORE_ABOVE(placement.above.length)}
              </button>
            )}
            {placement.below.length > 0 && (
              <button
                type="button"
                className={jumpClass}
                onClick={() => jumpTo(placement.below[0])}
              >
                <LuArrowDown size={14} aria-hidden />
                {ROTATION_PASTE_MORE_BELOW(placement.below.length)}
              </button>
            )}
          </div>
        )}
    </div>
  );
};

export default RotationAnchoredPanel;
